import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `roles` domain — the role list and the permissions
 * dialog it opens.
 *
 * Two facts drive most of the copy:
 *
 *   1. `deleteRole` (apps/admin/src/repo/roles.ts:93-103) removes the row and
 *      its permission links with NO check for staff still pointing at it. The
 *      admin row keeps the dangling `role_id`, the gateway resolver asks
 *      svc-admin, gets zero matching grants, and every permission check
 *      answers no — the person is locked out of the whole backoffice.
 *   2. `setRolePermissions` (same file, 125-181) REPLACES the set inside one
 *      transaction. Unticking is revoking; there is no additive mode.
 *
 * The permission catalogue itself holds concrete `resource:action` rows only
 * (scripts/seed-rbac.ts:44-46 inserts RESOURCES x ACTIONS), so the wildcard
 * forms the platform understands are not offerable as checkboxes here.
 */
export const rolesGuide: DomainGuide = {
  domain: 'roles',
  pages: [
    {
      id: 'roles.list',
      route: '/roles',
      // Gateway: `roles` reads = admin:read, writes = role:update
      // (api-gateway/src/upstreams.ts:375-376). The read gate is deliberately
      // admin:read, not role:read.
      permission: 'admin:read',
      status: 'live',
      fields: [
        { key: 'code', kind: 'text' },
        { key: 'name', kind: 'text' },
        { key: 'level', kind: 'text' },
        { key: 'permissionCount', kind: 'count' },
        { key: 'isSystem', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 1,
    },
    {
      id: 'roles.permissions',
      route: '/roles → Permissions',
      // Opening it only needs admin:read (GET /permissions and
      // GET /roles/:id/permissions); Save is a PUT, so role:update.
      permission: 'role:update',
      status: 'live',
      fields: [
        { key: 'resource', kind: 'text' },
        { key: 'action', kind: 'text' },
        { key: 'selected', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 2,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
